<script setup lang="ts">
	import type { ClassNameValue } from 'tailwind-merge'

	type statConfigClas = Partial<{
		base: ClassNameValue
		default: ClassNameValue
		display: ClassNameValue
		gap: ClassNameValue
		rounded: ClassNameValue
		bg: ClassNameValue
		border: ClassNameValue
		borderColor: ClassNameValue
		shadow: ClassNameValue
		height: ClassNameValue
		position: ClassNameValue
		header: Partial<{
			default: ClassNameValue
			paddingBlock: ClassNameValue
			color: ClassNameValue
			font: ClassNameValue
			size: ClassNameValue
			lineHeight: ClassNameValue
			marginBlock: ClassNameValue
			container: Partial<{
				base: ClassNameValue
				default: ClassNameValue
				marginBlock: ClassNameValue
				paddingInline: ClassNameValue
				paddingBlock: ClassNameValue
				border: ClassNameValue
			}>
		}>
	}>

	interface Props {
		title?: string | null | undefined
		headerClass?: ClassNameValue
		loading?: boolean
		ui?: statConfigClas
	}

	defineProps<Props>()

	const slots = useSlots()

	const configCls: statConfigClas = {
		default: '',
		display: 'flex flex-col',
		gap: 'gap-3',
		rounded: ' sm:rounded-br rounded-bl',
		shadow: 'shadow-light',
		bg: 'sm:bg-white',
		borderColor: 'border-gray-100',
		border: 'lg:border',
		height: 'sm:min-h-[251px]',
		header: {
			default: '',
			paddingBlock: 'py-0',
			color: 'text-blue-950',
			font: 'font-medium',
			size: 'text-base',
			lineHeight: 'leading-[18.40px]',
			marginBlock: 'mb-0',
			container: {
				default: '',
				marginBlock: 'mb-5',
				paddingInline: 'px-5',
				paddingBlock: 'md:py-8',
				border: 'border-b'
			}
		}
	}
	const attr = useAttrs()
	const attrClass = computed(() => attr.class as ClassNameValue)
</script>
<template>
	<loaders-skeleton v-if="loading" :count="1" :max-width="false" />
	<div
		v-else
		:class="
			cn(
				ui?.bg ?? configCls.bg,
				ui?.border ?? configCls.border,
				ui?.borderColor ?? configCls.borderColor,
				ui?.base ?? '',
				ui?.default ?? configCls.default,
				ui?.display ?? configCls.display,
				ui?.gap ?? configCls.gap,
				ui?.height ?? configCls.height,
				ui?.rounded ?? configCls.rounded,
				ui?.shadow ?? configCls.shadow,
				ui?.position ?? configCls.position,
				attrClass
			)
		"
	>
		<slot
			name="header"
			class-name="border-b md:py-8 px-5 md:mb-5"
			:h-class="
				cn(
					ui?.header?.color ?? configCls.header?.color,
					ui?.header?.default ?? configCls.header?.default,
					ui?.header?.font ?? configCls.header?.font,
					ui?.header?.lineHeight ?? configCls.header?.lineHeight,
					ui?.header?.marginBlock ?? configCls.header?.marginBlock,
					ui?.header?.paddingBlock ?? configCls.header?.paddingBlock,
					ui?.header?.size ?? configCls.header?.size
				)
			"
		>
			<header
				v-if="Boolean(title) || slots.header"
				:class="
					cn(
						headerClass,
						ui?.header?.container?.base ?? configCls.header?.container?.base,
						ui?.header?.container?.border ?? configCls.header?.container?.border,
						ui?.header?.container?.default ?? configCls.header?.container?.default,
						ui?.header?.container?.marginBlock ?? configCls.header?.container?.marginBlock,
						ui?.header?.container?.paddingBlock ?? configCls.header?.container?.paddingBlock,
						ui?.header?.container?.paddingInline ?? configCls.header?.container?.paddingInline
					)
				"
			>
				<h2
					:class="
						cn(
							ui?.header?.color ?? configCls.header?.color,
							ui?.header?.default ?? configCls.header?.default,
							ui?.header?.font ?? configCls.header?.font,
							ui?.header?.lineHeight ?? configCls.header?.lineHeight,
							ui?.header?.marginBlock ?? configCls.header?.marginBlock,
							ui?.header?.paddingBlock ?? configCls.header?.paddingBlock,
							ui?.header?.size ?? configCls.header?.size
						)
					"
				>
					{{ title || '' }}
				</h2>
			</header>
		</slot>
		<slot />
	</div>
</template>
